import { forwardRef, useId, type ReactNode } from 'react'
import { Link } from 'react-router-dom'

import type { FunnelStage, FunnelStageFigures, FunnelStageKey } from '@/api/types'
import { Card, Icon } from '@/design'

import {
  formatCount,
  formatRate,
  isMeasured,
  measuredBefore,
  NO_FIGURE,
  NOT_MEASURED,
  noEventsReason,
  previousStepComparable,
  stepComparable,
} from './figures'

/**
 * The stages whose losses become failed or cancelled orders, which the
 * Abandonment page lists: a session lost before payment never made an order.
 */
const ABANDONMENT_STAGES: ReadonlySet<FunnelStageKey> = new Set(['payment_attempted', 'purchase_completed'])

function Figures({ figures, above, comparable }: { figures: FunnelStageFigures; above: string | null; comparable: boolean }) {
  return (
    <dl className="fun-detail__figures">
      <div>
        <dt>Users</dt>
        <dd>{formatCount(figures.users)}</dd>
      </div>
      <div>
        <dt>Sessions</dt>
        <dd>{formatCount(figures.sessions)}</dd>
      </div>
      {above ? (
        <>
          <div>
            <dt>Conversion from {above}</dt>
            <dd>{comparable ? formatRate(figures.stageConversionRate) : NO_FIGURE}</dd>
          </div>
          <div>
            <dt>Lost after {above}</dt>
            <dd>
              {comparable && figures.dropOff !== null
                ? `${formatCount(figures.dropOff)} (${formatRate(figures.dropOffRate)})`
                : NO_FIGURE}
            </dd>
          </div>
        </>
      ) : null}
      <div>
        <dt>Conversion from visitors</dt>
        <dd>{formatRate(figures.totalConversionRate)}</dd>
      </div>
    </dl>
  )
}

/** One period's column: its heading names it, and a stage with no events then reads "Not measured". */
function Period({ title, children }: { title: string; children: ReactNode }) {
  const headingId = useId()
  return (
    <section aria-labelledby={headingId}>
      <h3 id={headingId} className="fun-detail__heading">
        {title}
      </h3>
      {children}
    </section>
  )
}

/**
 * One stage's figures, this period and the previous, opened from the funnel or
 * from a `?stage=` link. The API does not split a stage any further; the card
 * says so rather than leaving the viewer to look for a breakdown. The wrapper
 * takes focus when the stage is opened from the funnel.
 */
export const StageDetail = forwardRef<
  HTMLDivElement,
  {
    stages: readonly FunnelStage[]
    stage: FunnelStageKey
    /** The comparison period's dates, "6 Aug – 2 Sep 2026". */
    previousWindow: string | null
    abandonmentHref: string
    onClose: () => void
  }
>(function StageDetail({ stages, stage: key, previousWindow, abandonmentHref, onClose }, ref) {
  const index = stages.findIndex((stage) => stage.key === key)
  const stage = stages[index]
  if (!stage) return null
  const above = index > 0 ? stages[index - 1] : undefined
  const aboveLabel = above ? above.label.toLowerCase() : null
  const before = stage.previous

  return (
    <div ref={ref} tabIndex={-1} className="fun-detail-focus">
      <Card
        className="fun-detail"
        title={`Stage: ${stage.label}`}
        subtitle="Measured sessions that reached this stage"
        actions={
          <button type="button" className="ci-button" data-variant="ghost" onClick={onClose}>
            Close
          </button>
        }
      >
        {isMeasured(stage) ? (
          <div className="fun-detail__periods">
            <Period title="This period">
              <Figures figures={stage} above={aboveLabel} comparable={stepComparable(stage, above)} />
            </Period>
            {before ? (
              <Period title={`Previous period${previousWindow ? `, ${previousWindow}` : ''}`}>
                {measuredBefore([stage]) ? (
                  <Figures figures={before} above={aboveLabel} comparable={previousStepComparable(stage, above)} />
                ) : (
                  <p className="fun-note">
                    {NOT_MEASURED}. {noEventsReason(stage.key, 'previous')}.
                  </p>
                )}
              </Period>
            ) : null}
          </div>
        ) : (
          <p className="fun-note">
            {NOT_MEASURED}. {noEventsReason(stage.key, 'this')}, so this stage cannot be counted. The stages after it
            still count what did arrive.
          </p>
        )}
        <p className="fun-note">
          Breakdowns of a stage by competition, device or traffic source are not available yet: the funnel data counts
          each stage across every measured session.
        </p>
        {ABANDONMENT_STAGES.has(stage.key) ? (
          <p className="fun-note">
            <Link className="fun-link" to={abandonmentHref}>
              See the failed and cancelled orders on Abandonment <Icon name="arrowRight" size={14} />
            </Link>
          </p>
        ) : null}
      </Card>
    </div>
  )
})
